import * as React from 'react';
import { Box, styled } from '@mui/material';
import { Control, Controller } from "react-hook-form";

interface OtpInputProps {
  name: string;
  control: Control<any>;
  length?: number;
}

const OtpInput: React.FC<OtpInputProps> = ({ name, control, length = 6 }) => {
  const inputRefs = React.useRef<HTMLInputElement[]>(new Array(length).fill(null));

  const focusInput = (targetIndex: number) => {
    const targetInput = inputRefs.current[targetIndex];
    if (targetInput) {
      targetInput.focus();
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    currentIndex: number,
    onChange: (value: string) => void,
    value: string,
  ) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      const newValue = value.split('');
      
      // If current cell has value, clear it
      if (newValue[currentIndex]) {
        newValue[currentIndex] = '';
        onChange(newValue.join(''));
      } 
      // If current cell is empty, move to previous cell and clear it
      else if (currentIndex > 0) {
        focusInput(currentIndex - 1);
        newValue[currentIndex - 1] = '';
        onChange(newValue.join(''));
      }
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    currentIndex: number,
    onChange: (value: string) => void,
    value: string,
  ) => {
    const digit = event.target.value.replace(/[^0-9]/g, '');
    if (!digit) return;

    const newValue = (value || '').split('');
    newValue[currentIndex] = digit[digit.length - 1];
    const finalValue = newValue.join('');
    onChange(finalValue);

    if (currentIndex < length - 1) {
      focusInput(currentIndex + 1);
    }
  };

  const handlePaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
    onChange: (value: string) => void,
  ) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text').replace(/[^0-9]/g, '');
    const pastedValue = pastedData.slice(0, length);
    onChange(pastedValue.padEnd(length, ''));
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
          {Array.from({ length }, (_, index) => (
            <React.Fragment key={index}>
              <InputElement
                ref={(ele) => {
                  inputRefs.current[index] = ele!;
                }}
                onKeyDown={(event) => handleKeyDown(event, index, onChange, value)}
                onChange={(event) => handleChange(event, index, onChange, value)}
                onPaste={(event) => handlePaste(event, onChange)}
                value={value?.[index] ?? ''}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                aria-label={`Digit ${index + 1} of OTP`}
              />
            </React.Fragment>
          ))}
        </Box>
      )}
    />
  );
};

const InputElement = styled('input')(({ theme }) => ({
  width: 50,
  height: 50,
  fontSize: '1rem',
  fontWeight: 400,
  lineHeight: 1.5,
  padding: theme.spacing(1, 0),
  borderRadius: theme.shape.borderRadius,
  textAlign: 'center',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  transition: theme.transitions.create(['border-color', 'box-shadow']),

  '&:hover': {
    borderColor: theme.palette.primary.main,
  },

  '&:focus': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${theme.palette.primary.main}25`,
    outline: 0,
  },
}));

export default OtpInput; 